import AdminModalShell from "./admin/AdminModalShell";
import { AlertCircle, Check, Save } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ConfirmationModal from "./ConfirmationModal";
import { useTheme } from "../contexts/ThemeContext";
import accountSummaryService from "../services/accountSummaryService";
import advisorAccountService from "../services/advisorAccountService";
import agreementsService from "../services/agreementsService";
import userConfigurationService, {
  ConfigData,
  ConfigValue,
  UserConfiguration,
  UserSettingsData,
} from "../services/UserConfigurationService";
import userConfigurationSchemaService, {
  ConfigurationSettingType,
  SchemaPropertyDescriptor,
  UserConfigurationSchemaDescriptor,
  normalizeConfigurationSettingType,
} from "../services/userConfigurationSchemaService";
import { formatCurrencyK, safeParseFloat } from "../utils/numberUtils";
import {
  CASH_BASED_DEPLOYMENT_KEY,
  cashDeploymentAdminInputToSettingValue,
  parseCashDeploymentAdminInput,
  type CashDeploymentAdminInput,
} from "../utils/cashDeploymentSettings";

interface UserConfigurationModalProps {
  appearance?: "default" | "alpha";
  isOpen: boolean;
  onClose: () => void;
  userId: string | null;
  userName?: string | null;
  editableUserName?: string | null;
  onUserNameSave?: (userId: string, userName: string) => Promise<void>;
  onSave?: () => void;
  configurationSchemaDescriptor?: UserConfigurationSchemaDescriptor | null;
}

type FlagValue = "yes" | "no";
type BooleanInputValue = "unset" | FlagValue;

type RetirementConfirmDirection = "to-yes" | "to-no" | "to-unset" | null;
type CustomerConfirmDirection = "to-yes" | "to-no" | "to-unset" | null;

type FormSnapshot = {
  ibkrAccountId: string;
  isbeta: BooleanInputValue;
  iscustomer: BooleanInputValue;
  isretirement: BooleanInputValue;
  tradingenabled: BooleanInputValue;
  userTradingEnabled: BooleanInputValue;
  cashBasedDeployment: CashDeploymentAdminInput;
  additionalRootValues: Record<string, string>;
  additionalUserSettingValues: Record<string, string>;
};

const shallowEqualStringRecord = (
  a: Record<string, string>,
  b: Record<string, string>
): boolean => {
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  for (const k of ak) {
    if (a[k] !== b[k]) return false;
  }
  return true;
};

// Keys consumed from data across the codebase; portfolioweight is intentionally dropped on save.
const KNOWN_KEYS = new Set(["isbeta", "iscustomer", "isretirement", "tradingenabled", "portfolioweight"]);
const BUILT_IN_ROOT_CONFIG_KEYS = new Set(["isbeta", "iscustomer", "isretirement", "tradingenabled"]);

const STANDARD_ADVISORY_AGREEMENT_NAME = "investment-advisory-agreement";
const RETIREMENT_ADVISORY_AGREEMENT_NAME = "investment-advisory-agreement-retirement";
const BUILT_IN_USER_SETTINGS_KEYS = new Set(["user_tradingenabled", CASH_BASED_DEPLOYMENT_KEY]);
const ADMIN_EDITABLE_USER_SETTINGS_KEYS = new Set([CASH_BASED_DEPLOYMENT_KEY]);
const CONFIG_FIELD_MAX_LENGTH = 125;
const isConfigKey = (key: string): boolean => key.length <= CONFIG_FIELD_MAX_LENGTH;
const isConfigValue = (value: unknown): value is ConfigValue =>
  typeof value === "boolean" ||
  (typeof value === "string" && value.length <= CONFIG_FIELD_MAX_LENGTH) ||
  (typeof value === "number" && Number.isSafeInteger(value));
const isConfigObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

type SyncStepStatus = "pending" | "in_progress" | "success" | "error";

interface SyncProgress {
  step1_saveConfig: SyncStepStatus;
  step2_verifyAccount: SyncStepStatus;
  step3_syncMapping: SyncStepStatus;
  errorMessage: string | null;
  subaccountsLoadProgress: { loaded: number; total: number } | null;
  ranSyncSteps: boolean;
}

type LinkedAccountStatus = "unknown" | "loading" | "linked" | "not_linked" | "error";

interface ConfigurationSettingDescriptor {
  name: string;
  type: ConfigurationSettingType;
}

const parseFlag = (value: unknown): FlagValue =>
  typeof value === "boolean"
    ? (value ? "yes" : "no")
    : String(value ?? "").trim().toLowerCase() === "true" ? "yes" : "no";

const parseBooleanInput = (value: unknown): BooleanInputValue =>
  value === undefined || value === null || String(value).trim() === ""
    ? "unset"
    : parseFlag(value);

const isNotFoundError = (err: any): boolean => (err.response?.status || err.status) === 404;

const getUserNameUpdateErrorMessage = (err: any): string => {
  const status = err.response?.status || err.status;
  if (status === 401) {
    return "Your session is not authorized. Sign in again before updating the name.";
  }
  if (status === 403) {
    return "Your authenticated user does not have permission to update this name.";
  }
  return err.response?.data?.error || err.message || "Unable to update the name. Try again.";
};

const formatSettingLabel = (name: string): string =>
  name === "enableTradeNotification" ? "Trade Notifications" : name
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const uniqueSettings = (properties: ConfigurationSettingDescriptor[]): ConfigurationSettingDescriptor[] => {
  const seen = new Set<string>();
  return properties.filter((property) => {
    if (seen.has(property.name)) return false;
    seen.add(property.name);
    return true;
  });
};

const toConfigurationSetting = (property: SchemaPropertyDescriptor): ConfigurationSettingDescriptor => ({
  name: property.name,
  type: normalizeConfigurationSettingType(property.type),
});

const getAdditionalRootSettings = (descriptor: UserConfigurationSchemaDescriptor | null): ConfigurationSettingDescriptor[] =>
  uniqueSettings(
    (descriptor?.rootLevelProperties ?? [])
      .filter((property) => !BUILT_IN_ROOT_CONFIG_KEYS.has(property.name))
      .map(toConfigurationSetting)
  );

const getUserSettingDescriptors = (descriptor: UserConfigurationSchemaDescriptor | null): ConfigurationSettingDescriptor[] =>
  uniqueSettings([
    { name: "user_tradingenabled", type: "boolean" },
    { name: CASH_BASED_DEPLOYMENT_KEY, type: "boolean" },
    ...(descriptor?.userSettingsProperties ?? []).map(toConfigurationSetting),
  ]);

const getInitialInputValue = (
  setting: ConfigurationSettingDescriptor,
  value: unknown
): string => {
  if (value === undefined || value === null) {
    return setting.type === "boolean" ? "unset" : "";
  }
  if (setting.type === "boolean") {
    return parseFlag(value);
  }
  return String(value);
};

const parseInputValue = (
  setting: ConfigurationSettingDescriptor,
  value: string | undefined
): ConfigValue | undefined => {
  const nextValue = value ?? (setting.type === "boolean" ? "unset" : "");
  if (setting.type === "boolean") {
    if (nextValue === "unset") return undefined;
    return nextValue === "yes" ? "true" : "false";
  }
  const trimmed = nextValue.trim();
  if (!trimmed) return undefined;
  if (setting.type === "integer") {
    if (!/^-?\d+$/.test(trimmed)) return undefined;
    const parsed = Number(trimmed);
    return Number.isSafeInteger(parsed) ? parsed : undefined;
  }
  return nextValue;
};

const getInputValidationError = (
  setting: ConfigurationSettingDescriptor,
  value: string | undefined
): string | null => {
  const nextValue = value ?? "";
  if (setting.type === "integer") {
    const trimmed = nextValue.trim();
    if (!trimmed) return null;
    if (!/^-?\d+$/.test(trimmed)) return "Enter a whole number.";
    if (!Number.isSafeInteger(Number(trimmed))) return "Number is outside the safe integer range.";
  }
  if (setting.type === "string" && nextValue.length > CONFIG_FIELD_MAX_LENGTH) {
    return `Text must be ${CONFIG_FIELD_MAX_LENGTH} characters or less.`;
  }
  return null;
};

// Blur on wheel so scrolling over a focused number input cannot mutate its value;
// admins must type the value explicitly. Matches the pattern in the strategy editors.
const preventScrollChange = (e: React.WheelEvent<HTMLInputElement>) => {
  (e.target as HTMLInputElement).blur();
};

const UserConfigurationModal: React.FC<UserConfigurationModalProps> = ({
  appearance = "default",
  isOpen,
  onClose,
  userId,
  userName,
  editableUserName,
  onUserNameSave,
  onSave,
  configurationSchemaDescriptor,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const canEditUserName = onUserNameSave !== undefined;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<UserConfiguration | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  // Snapshot of the form state at the last clean point (initial load or successful save).
  // Used by the unsaved-changes guard on close. Null when nothing is loaded yet.
  const cleanSnapshotRef = useRef<FormSnapshot | null>(null);

  // Form state
  const [userNameValue, setUserNameValue] = useState("");
  const [userNameSaving, setUserNameSaving] = useState(false);
  const [userNameError, setUserNameError] = useState<string | null>(null);
  const [userNameSaved, setUserNameSaved] = useState(false);
  const [ibkrAccountId, setIbkrAccountId] = useState("");
  const [isbeta, setIsbeta] = useState<BooleanInputValue>("unset");
  const [iscustomer, setIscustomer] = useState<BooleanInputValue>("unset");
  const [isretirement, setIsretirement] = useState<BooleanInputValue>("unset");
  const [tradingenabled, setTradingenabled] = useState<BooleanInputValue>("unset");
  const [userTradingEnabled, setUserTradingEnabled] = useState<BooleanInputValue>("unset");
  const [cashBasedDeployment, setCashBasedDeployment] = useState<CashDeploymentAdminInput>("unset");
  const [configurationSchema, setConfigurationSchema] = useState<UserConfigurationSchemaDescriptor | null>(null);
  const [additionalRootValues, setAdditionalRootValues] = useState<Record<string, string>>({});
  const [additionalUserSettingValues, setAdditionalUserSettingValues] = useState<Record<string, string>>({});
  const [preservedAdditionalData, setPreservedAdditionalData] = useState<ConfigData>({});
  const [preservedUserSettings, setPreservedUserSettings] = useState<UserSettingsData>({});

  // isretirement confirmation flow
  const [retirementConfirm, setRetirementConfirm] = useState<RetirementConfirmDirection>(null);

  // iscustomer confirmation + async IBKR balance check
  const [customerConfirm, setCustomerConfirm] = useState<CustomerConfirmDirection>(null);
  const [customerCheckLoading, setCustomerCheckLoading] = useState(false);
  const [customerCheckError, setCustomerCheckError] = useState<string | null>(null);
  // Net liquidation value fetched during to-yes check, shown in confirmation
  const [customerNetLiq, setCustomerNetLiq] = useState<number | null>(null);
  const latestUserIdRef = useRef<string | null>(userId);
  const latestIsOpenRef = useRef<boolean>(isOpen);
  const userNameContextUserIdRef = useRef<string | null>(null);
  const cleanUserNameRef = useRef("");
  const userNameRequestIdRef = useRef(0);
  const configurationRequestIdRef = useRef(0);
  const customerCheckRequestIdRef = useRef(0);

  // 3-step sync progress state
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const syncRequestIdRef = useRef(0);

  // Per-user linked-account pre-check
  const [linkedAccountStatus, setLinkedAccountStatus] = useState<LinkedAccountStatus>("unknown");
  const linkedAccountCheckIdRef = useRef(0);
  // Snapshot of ibkrAccountId loaded from config, used to detect changes on save
  const loadedIbkrAccountIdRef = useRef<string>("");
  // Full payload snapshot persisted by step 1, used to detect form drift on retry
  const savedPayloadRef = useRef<{ ibkrAccountId: string; data: ConfigData | null }>({
    ibkrAccountId: "",
    data: null,
  });

  const isCurrentCustomerCheckRequest = (requestId: number, requestUserId: string) =>
    customerCheckRequestIdRef.current === requestId &&
    latestUserIdRef.current === requestUserId &&
    latestIsOpenRef.current;

  const resetForm = useCallback(() => {
    setUserNameValue("");
    setUserNameSaving(false);
    setUserNameError(null);
    setUserNameSaved(false);
    userNameContextUserIdRef.current = null;
    cleanUserNameRef.current = "";
    setIbkrAccountId("");
    setIsbeta("unset");
    setIscustomer("unset");
    setIsretirement("unset");
    setTradingenabled("unset");
    setUserTradingEnabled("unset");
    setCashBasedDeployment("unset");
    setConfigurationSchema(null);
    setAdditionalRootValues({});
    setAdditionalUserSettingValues({});
    setPreservedAdditionalData({});
    setPreservedUserSettings({});
    setConfig(null);
    setError(null);
    setRetirementConfirm(null);
    setShowDeleteConfirm(false);
    setShowDiscardConfirm(false);
    setCustomerConfirm(null);
    setCustomerCheckLoading(false);
    setCustomerCheckError(null);
    setCustomerNetLiq(null);
    setSyncProgress(null);
    setLinkedAccountStatus("unknown");
    loadedIbkrAccountIdRef.current = "";
    savedPayloadRef.current = { ibkrAccountId: "", data: null };
    cleanSnapshotRef.current = null;
  }, []);

  const fetchLinkedAccountStatus = useCallback(async (checkUserId: string, expectedIbkrAccountId: string) => {
    const checkId = ++linkedAccountCheckIdRef.current;
    setLinkedAccountStatus("loading");
    try {
      const linkedAccount = await advisorAccountService.getLinkedAccount(checkUserId);
      if (linkedAccountCheckIdRef.current !== checkId || latestUserIdRef.current !== checkUserId || !latestIsOpenRef.current) return;
      const expected = expectedIbkrAccountId.trim().toLowerCase();
      const actual = linkedAccount.ibkrAccountId.trim().toLowerCase();
      setLinkedAccountStatus(actual === expected ? "linked" : "not_linked");
    } catch (err: any) {
      if (linkedAccountCheckIdRef.current !== checkId || latestUserIdRef.current !== checkUserId || !latestIsOpenRef.current) return;
      const status = err.response?.status || err.status;
      if (status === 404) {
        setLinkedAccountStatus("not_linked");
      } else {
        setLinkedAccountStatus("error");
      }
    }
  }, []);

  const fetchConfiguration = useCallback(async (id: string) => {
    const requestId = ++configurationRequestIdRef.current;
    const isCurrentConfigurationRequest = () =>
      configurationRequestIdRef.current === requestId &&
      latestUserIdRef.current === id &&
      latestIsOpenRef.current;

    try {
      setLoading(true);
      setError(null);
      let schemaDescriptor: UserConfigurationSchemaDescriptor | null = configurationSchemaDescriptor ?? null;
      setConfigurationSchema(schemaDescriptor);
      try {
        const schema = await userConfigurationSchemaService.getActiveSchema();
        if (!isCurrentConfigurationRequest()) return;
        schemaDescriptor = schema.descriptor;
        setConfigurationSchema(schemaDescriptor);
      } catch (err: any) {
        if (!isNotFoundError(err)) {
          console.warn("Failed to fetch user configuration schema:", err);
        }
        if (!isCurrentConfigurationRequest()) return;
        setConfigurationSchema(schemaDescriptor);
      }

      const additionalRootSettings = getAdditionalRootSettings(schemaDescriptor);
      const userSettingDescriptors = getUserSettingDescriptors(schemaDescriptor);
      const additionalRootSettingByName = new Map(additionalRootSettings.map((setting) => [setting.name, setting]));
      const userSettingByName = new Map(userSettingDescriptors.map((setting) => [setting.name, setting]));

      try {
        const data = await userConfigurationService.getUserConfiguration(id);
        if (!isCurrentConfigurationRequest()) return;
        setConfig(data);
        const loadedAccountId = data.ibkrAccountId || "";
        setIbkrAccountId(loadedAccountId);
        loadedIbkrAccountIdRef.current = loadedAccountId;
        if (data.data) {
          setIsbeta(parseBooleanInput(data.data.isbeta));
          setIscustomer(parseBooleanInput(data.data.iscustomer));
          setIsretirement(parseBooleanInput(data.data.isretirement));
          setTradingenabled(parseBooleanInput(data.data.tradingenabled));
          let loadedUserTradingEnabled: BooleanInputValue = "unset";
          let loadedCashBasedDeployment: CashDeploymentAdminInput = "unset";
          const loadedAdditionalRootValues: Record<string, string> = {};
          const loadedAdditionalUserSettings: Record<string, string> = Object.fromEntries(
            userSettingDescriptors
              .filter((setting) => !BUILT_IN_USER_SETTINGS_KEYS.has(setting.name))
              .map((setting) => [setting.name, getInitialInputValue(setting, undefined)])
          );
          const preservedAdditional: ConfigData = {};
          const preservedSettings: UserSettingsData = {};
          for (const [key, value] of Object.entries(data.data)) {
            if (KNOWN_KEYS.has(key)) continue;
            const rootSetting = additionalRootSettingByName.get(key);
            if (rootSetting) {
              loadedAdditionalRootValues[key] = getInitialInputValue(rootSetting, value);
              continue;
            }
            if (key === "userSettings") {
              if (isConfigObject(value)) {
                for (const [sk, sv] of Object.entries(value)) {
                  if (sk === "user_tradingenabled") {
                    loadedUserTradingEnabled = parseFlag(sv);
                  } else if (sk === CASH_BASED_DEPLOYMENT_KEY) {
                    loadedCashBasedDeployment = parseCashDeploymentAdminInput(sv);
                  } else if (isConfigKey(sk) && isConfigValue(sv)) {
                    const userSetting = userSettingByName.get(sk);
                    if (userSetting && !BUILT_IN_USER_SETTINGS_KEYS.has(sk)) {
                      loadedAdditionalUserSettings[sk] = getInitialInputValue(userSetting, sv);
                    }
                    preservedSettings[sk] = sv;
                  }
                }
              }
              continue;
            }
            if (isConfigKey(key) && isConfigValue(value)) {
              preservedAdditional[key] = value;
            }
          }
          additionalRootSettings.forEach((setting) => {
            if (!(setting.name in loadedAdditionalRootValues)) {
              loadedAdditionalRootValues[setting.name] = getInitialInputValue(setting, undefined);
            }
          });
          setUserTradingEnabled(loadedUserTradingEnabled);
          setCashBasedDeployment(loadedCashBasedDeployment);
          setAdditionalRootValues(loadedAdditionalRootValues);
          setAdditionalUserSettingValues(loadedAdditionalUserSettings);
          setPreservedAdditionalData(preservedAdditional);
          setPreservedUserSettings(preservedSettings);
          cleanSnapshotRef.current = {
            ibkrAccountId: loadedAccountId,
            isbeta: parseBooleanInput(data.data.isbeta),
            iscustomer: parseBooleanInput(data.data.iscustomer),
            isretirement: parseBooleanInput(data.data.isretirement),
            tradingenabled: parseBooleanInput(data.data.tradingenabled),
            userTradingEnabled: loadedUserTradingEnabled,
            cashBasedDeployment: loadedCashBasedDeployment,
            additionalRootValues: { ...loadedAdditionalRootValues },
            additionalUserSettingValues: { ...loadedAdditionalUserSettings },
          };
        } else {
          const emptyRootValues = Object.fromEntries(
            additionalRootSettings.map((setting) => [setting.name, getInitialInputValue(setting, undefined)])
          );
          const emptyUserSettingValues = Object.fromEntries(
            userSettingDescriptors
              .filter((setting) => !BUILT_IN_USER_SETTINGS_KEYS.has(setting.name))
              .map((setting) => [setting.name, getInitialInputValue(setting, undefined)])
          );
          setIsbeta("unset");
          setIscustomer("unset");
          setIsretirement("unset");
          setTradingenabled("unset");
          setUserTradingEnabled("unset");
          setCashBasedDeployment("unset");
          setAdditionalRootValues(emptyRootValues);
          setAdditionalUserSettingValues(emptyUserSettingValues);
          setPreservedAdditionalData({});
          setPreservedUserSettings({});
          cleanSnapshotRef.current = {
            ibkrAccountId: loadedAccountId,
            isbeta: "unset",
            iscustomer: "unset",
            isretirement: "unset",
            tradingenabled: "unset",
            userTradingEnabled: "unset",
            cashBasedDeployment: "unset",
            additionalRootValues: { ...emptyRootValues },
            additionalUserSettingValues: { ...emptyUserSettingValues },
          };
        }
        // Fire linked-account pre-check if ibkrAccountId is set
        if (data.ibkrAccountId) {
          fetchLinkedAccountStatus(id, data.ibkrAccountId);
        }
      } catch (err: any) {
        const status = err.response?.status || err.status;
        if (status === 404) {
          if (!isCurrentConfigurationRequest()) return;
          console.log(`No configuration found for user ${id}, showing empty form`);
          const emptyRootValues = Object.fromEntries(
            additionalRootSettings.map((setting) => [setting.name, getInitialInputValue(setting, undefined)])
          );
          const emptyUserSettingValues = Object.fromEntries(
            userSettingDescriptors
              .filter((setting) => !BUILT_IN_USER_SETTINGS_KEYS.has(setting.name))
              .map((setting) => [setting.name, getInitialInputValue(setting, undefined)])
          );
          setConfig(null);
          setIbkrAccountId("");
          loadedIbkrAccountIdRef.current = "";
          setIsbeta("unset");
          setIscustomer("unset");
          setIsretirement("unset");
          setTradingenabled("unset");
          setUserTradingEnabled("unset");
          setCashBasedDeployment("unset");
          setAdditionalRootValues(emptyRootValues);
          setAdditionalUserSettingValues(emptyUserSettingValues);
          setPreservedAdditionalData({});
          setPreservedUserSettings({});
          cleanSnapshotRef.current = {
            ibkrAccountId: "",
            isbeta: "unset",
            iscustomer: "unset",
            isretirement: "unset",
            tradingenabled: "unset",
            userTradingEnabled: "unset",
            cashBasedDeployment: "unset",
            additionalRootValues: { ...emptyRootValues },
            additionalUserSettingValues: { ...emptyUserSettingValues },
          };
        } else {
          throw err;
        }
      }
    } catch (err) {
      if (!isCurrentConfigurationRequest()) return;
      console.error("Failed to fetch user configuration:", err);
      setError("Failed to fetch configuration");
    } finally {
      if (isCurrentConfigurationRequest()) {
        setLoading(false);
      }
    }
  }, [configurationSchemaDescriptor, fetchLinkedAccountStatus]);

  useEffect(() => {
    const previousUserId = latestUserIdRef.current;
    const didUserContextChange =
      isOpen &&
      !!userId &&
      !!previousUserId &&
      previousUserId !== userId;

    latestUserIdRef.current = userId;
    latestIsOpenRef.current = isOpen;
    if (!isOpen || !userId || didUserContextChange) {
      // Invalidate in-flight customer checks when modal closes or user context changes.
      customerCheckRequestIdRef.current += 1;
      setCustomerCheckLoading(false);
      setCustomerConfirm(null);
      setCustomerCheckError(null);
      setCustomerNetLiq(null);
      // Invalidate in-flight configuration loads when modal closes or user context changes.
      configurationRequestIdRef.current += 1;
      // Invalidate in-flight name updates.
      userNameRequestIdRef.current += 1;
      setUserNameSaving(false);
      // Invalidate in-flight linked-account checks
      linkedAccountCheckIdRef.current += 1;
      setLinkedAccountStatus("unknown");
      // Invalidate any in-flight sync by bumping the request ID
      syncRequestIdRef.current += 1;
      setSyncProgress(null);
    }

    if (didUserContextChange) {
      resetForm();
    }

    if (isOpen && userId) {
      fetchConfiguration(userId);
    } else {
      resetForm();
    }
  }, [fetchConfiguration, isOpen, resetForm, userId]);

  useEffect(() => {
    if (!isOpen || !userId || !canEditUserName) {
      userNameContextUserIdRef.current = null;
      cleanUserNameRef.current = "";
      setUserNameValue("");
      setUserNameError(null);
      setUserNameSaved(false);
      return;
    }

    const nextUserName = editableUserName ?? "";
    if (userNameContextUserIdRef.current !== userId) {
      userNameContextUserIdRef.current = userId;
      cleanUserNameRef.current = nextUserName;
      setUserNameValue(nextUserName);
      setUserNameError(null);
      setUserNameSaved(false);
      return;
    }

    // Accept a server-refreshed name only when the admin has no newer local edit.
    if (nextUserName !== cleanUserNameRef.current && userNameValue === cleanUserNameRef.current) {
      cleanUserNameRef.current = nextUserName;
      setUserNameValue(nextUserName);
    }
  }, [canEditUserName, editableUserName, isOpen, userId, userNameValue]);

  const handleUserNameUpdate = async () => {
    if (
      !userId ||
      !onUserNameSave ||
      userNameSaving ||
      userNameValue === cleanUserNameRef.current
    ) {
      return;
    }

    const requestUserId = userId;
    const requestId = ++userNameRequestIdRef.current;
    setUserNameSaving(true);
    setUserNameError(null);
    setUserNameSaved(false);

    try {
      await onUserNameSave(requestUserId, userNameValue);
      if (
        userNameRequestIdRef.current !== requestId ||
        latestUserIdRef.current !== requestUserId ||
        !latestIsOpenRef.current
      ) {
        return;
      }
      cleanUserNameRef.current = userNameValue;
      setUserNameSaved(true);
      onSave?.();
    } catch (err: any) {
      if (
        userNameRequestIdRef.current !== requestId ||
        latestUserIdRef.current !== requestUserId ||
        !latestIsOpenRef.current
      ) {
        return;
      }
      setUserNameError(getUserNameUpdateErrorMessage(err));
    } finally {
      if (
        userNameRequestIdRef.current === requestId &&
        latestUserIdRef.current === requestUserId &&
        latestIsOpenRef.current
      ) {
        setUserNameSaving(false);
      }
    }
  };

  // isretirement handlers

  const handleRetirementChange = (newValue: BooleanInputValue) => {
    if (newValue === isretirement) return;
    setRetirementConfirm(newValue === "yes" ? "to-yes" : newValue === "no" ? "to-no" : "to-unset");
  };

  const handleRetirementConfirm = () => {
    if (retirementConfirm === "to-yes") {
      setIsretirement("yes");
    } else if (retirementConfirm === "to-no") {
      setIsretirement("no");
    } else if (retirementConfirm === "to-unset") {
      setIsretirement("unset");
    }
    setRetirementConfirm(null);
  };

  const handleRetirementCancel = () => {
    setRetirementConfirm(null);
  };

  // iscustomer handlers
  // Setting to Yes requires a fresh IBKR account summary fetch and netLiquidationSecurity > 10000.

  const handleCustomerChange = async (newValue: BooleanInputValue) => {
    if (newValue === iscustomer) return;

    if (newValue === "no" || newValue === "unset") {
      customerCheckRequestIdRef.current += 1;
      setCustomerConfirm(newValue === "no" ? "to-no" : "to-unset");
      setCustomerCheckLoading(false);
      setCustomerCheckError(null);
      setCustomerNetLiq(null);
      return;
    }

    // to-yes: validate isretirement parity, fetch account summary + agreements in parallel
    if (!userId) return;

    // Persisted vs in-form isretirement must match. The user signed the advisory agreement
    // against the persisted value, so admin must save any isretirement change separately
    // before flipping iscustomer.
    const persistedIsretirement = parseBooleanInput(config?.data?.isretirement);
    if (persistedIsretirement !== isretirement) {
      setCustomerCheckError(
        "isretirement has unsaved changes. Save isretirement separately before marking as customer."
      );
      return;
    }
    if (persistedIsretirement === "unset") {
      setCustomerCheckError(
        "isretirement must be set before marking as customer."
      );
      return;
    }
    const requiredAgreementName =
      persistedIsretirement === "yes"
        ? RETIREMENT_ADVISORY_AGREEMENT_NAME
        : STANDARD_ADVISORY_AGREEMENT_NAME;

    const requestUserId = userId;
    const requestId = ++customerCheckRequestIdRef.current;
    setCustomerCheckLoading(true);
    setCustomerCheckError(null);
    setCustomerNetLiq(null);

    try {
      // maxAgeMs=0 forces a fresh fetch, same as the refresh path in AdminAccountSummary
      const [summaryResult, agreementsResult] = await Promise.allSettled([
        accountSummaryService.getAdminUserAccountSummary(userId, 0, true),
        agreementsService.getUserAgreements(userId),
      ]);
      if (!isCurrentCustomerCheckRequest(requestId, requestUserId)) return;

      if (summaryResult.status === "rejected") {
        const status =
          summaryResult.reason?.response?.status || summaryResult.reason?.status;
        setCustomerCheckError(
          status === 404
            ? "No IBKR account summary found. Connect account before marking as customer."
            : "Failed to fetch account summary. Try again."
        );
        return;
      }
      if (agreementsResult.status === "rejected") {
        setCustomerCheckError("Failed to fetch user agreements. Try again.");
        return;
      }

      // API may return numeric fields as strings; parse explicitly before comparing or displaying.
      const netLiq = safeParseFloat(summaryResult.value.netLiquidationSecurity);
      if (netLiq <= 10000) {
        setCustomerCheckError(
          `Net liquidation (${formatCurrencyK(netLiq)}) is below the $10,000 minimum required to mark as customer.`
        );
        return;
      }

      const hasSignedRequiredAgreement = agreementsResult.value.some(
        (a) => a.agreementName === requiredAgreementName
      );
      if (!hasSignedRequiredAgreement) {
        // Distinguish "not signed at all" from "signed the other variant" so admin sees the
        // actual root cause and can direct the user to re-sign the correct one on Profile Step 4.
        const otherVariantName =
          persistedIsretirement === "yes"
            ? STANDARD_ADVISORY_AGREEMENT_NAME
            : RETIREMENT_ADVISORY_AGREEMENT_NAME;
        const hasSignedOtherVariant = agreementsResult.value.some(
          (a) => a.agreementName === otherVariantName
        );
        if (hasSignedOtherVariant) {
          setCustomerCheckError(
            persistedIsretirement === "yes"
              ? "Wrong advisory agreement on file. User signed the standard agreement but isretirement is true; user must sign the retirement variant on Profile Step 4."
              : "Wrong advisory agreement on file. User signed the retirement (IRA) agreement but isretirement is false; user must sign the standard variant on Profile Step 4."
          );
        } else {
          setCustomerCheckError(
            "Investment Advisory Agreement not signed. User must complete Step 4 on Profile before being marked as customer."
          );
        }
        return;
      }

      setCustomerNetLiq(netLiq);
      setCustomerConfirm("to-yes");
    } finally {
      if (!isCurrentCustomerCheckRequest(requestId, requestUserId)) return;
      setCustomerCheckLoading(false);
    }
  };

  const handleCustomerConfirm = () => {
    if (customerConfirm === "to-yes") {
      setIscustomer("yes");
    } else if (customerConfirm === "to-no") {
      setIscustomer("no");
      setTradingenabled("no");
    } else if (customerConfirm === "to-unset") {
      setIscustomer("unset");
      setTradingenabled("unset");
    }
    setCustomerConfirm(null);
    setCustomerNetLiq(null);
    setCustomerCheckError(null);
  };

  const handleCustomerCancel = () => {
    setCustomerConfirm(null);
    setCustomerNetLiq(null);
    setCustomerCheckError(null);
  };

  const buildDataPayload = (): ConfigData | null => {
    const dataObject: ConfigData = { ...preservedAdditionalData };
    if (isbeta !== "unset") {
      dataObject.isbeta = isbeta === "yes" ? "true" : "false";
    }
    if (iscustomer !== "unset") {
      dataObject.iscustomer = iscustomer === "yes" ? "true" : "false";
    }
    if (tradingenabled !== "unset") {
      dataObject.tradingenabled = iscustomer === "yes" && tradingenabled === "yes" ? "true" : "false";
    }
    if (isretirement !== "unset") {
      dataObject.isretirement = isretirement === "yes" ? "true" : "false";
    }
    additionalRootSettings.forEach((setting) => {
      const value = parseInputValue(setting, additionalRootValues[setting.name]);
      if (value !== undefined) {
        dataObject[setting.name] = value;
      }
    });

    const settings: UserSettingsData = { ...preservedUserSettings };
    if (userTradingEnabled !== "unset") {
      settings.user_tradingenabled = userTradingEnabled === "yes";
    }
    if (cashBasedDeployment !== "unset") {
      const cashValue = cashDeploymentAdminInputToSettingValue(cashBasedDeployment);
      if (cashValue !== undefined) {
        settings[CASH_BASED_DEPLOYMENT_KEY] = cashValue;
      }
    } else {
      delete settings[CASH_BASED_DEPLOYMENT_KEY];
    }
    if (Object.keys(settings).length > 0) {
      dataObject.userSettings = settings;
    }
    return Object.keys(dataObject).length > 0 ? dataObject : null;
  };

  const shouldRunSyncSteps = (): boolean => {
    const trimmedInput = ibkrAccountId.trim();
    if (!trimmedInput) return false;
    const loadedValue = loadedIbkrAccountIdRef.current.trim();
    const isUnchanged = trimmedInput.toLowerCase() === loadedValue.toLowerCase() && loadedValue !== "";
    if (isUnchanged && linkedAccountStatus === "linked") return false;
    return true;
  };

  const isCurrentSyncRequest = (requestId: number, requestUserId: string) =>
    syncRequestIdRef.current === requestId &&
    latestUserIdRef.current === requestUserId &&
    latestIsOpenRef.current;

  const handleSave = async () => {
    if (!userId) return;

    const requestId = ++syncRequestIdRef.current;
    const saveUserId = userId;
    const runSync = shouldRunSyncSteps();
    const initialProgress: SyncProgress = {
      step1_saveConfig: "in_progress",
      step2_verifyAccount: "pending",
      step3_syncMapping: "pending",
      errorMessage: null,
      subaccountsLoadProgress: null,
      ranSyncSteps: runSync,
    };
    setSyncProgress(initialProgress);
    setError(null);

    // Step 1: Save configuration
    try {
      const dataPayload = buildDataPayload();
      let savedConfig: UserConfiguration;
      if (config) {
        savedConfig = await userConfigurationService.updateUserConfiguration(saveUserId, {
          ibkrAccountId: ibkrAccountId.trim() || null,
          data: dataPayload,
        });
      } else {
        savedConfig = await userConfigurationService.createUserConfiguration({
          userId: saveUserId,
          ibkrAccountId: ibkrAccountId.trim() || null,
          data: dataPayload,
        });
      }
      if (!isCurrentSyncRequest(requestId, saveUserId)) return;
      setConfig(savedConfig);
      setIsbeta(parseBooleanInput(savedConfig.data?.isbeta));
      savedPayloadRef.current = { ibkrAccountId: ibkrAccountId.trim(), data: savedConfig.data };
      cleanSnapshotRef.current = {
        ibkrAccountId,
        isbeta: parseBooleanInput(savedConfig.data?.isbeta),
        iscustomer,
        isretirement,
        tradingenabled,
        userTradingEnabled,
        cashBasedDeployment,
        additionalRootValues: { ...additionalRootValues },
        additionalUserSettingValues: { ...additionalUserSettingValues },
      };
    } catch (err: any) {
      if (!isCurrentSyncRequest(requestId, saveUserId)) return;
      console.error("Failed to save user configuration:", err);
      setSyncProgress(prev => prev ? {
        ...prev,
        step1_saveConfig: "error",
        errorMessage: err.response?.data?.error || err.message || "Failed to save configuration",
      } : null);
      return;
    }

    if (!isCurrentSyncRequest(requestId, saveUserId)) return;
    setSyncProgress(prev => prev ? { ...prev, step1_saveConfig: "success" } : null);
    onSave?.();

    if (!runSync) {
      // No sync needed -- safe to promote snapshot now
      loadedIbkrAccountIdRef.current = ibkrAccountId.trim();
      return;
    }

    // Account changed and sync is required -- invalidate linked status so
    // shouldRunSyncSteps() won't skip sync if step 2/3 fails and admin re-saves
    setLinkedAccountStatus("not_linked");

    // Step 2: Verify account exists at IBKR
    await runStep2(requestId, saveUserId);
  };

  const runStep2 = async (requestId: number, saveUserId: string) => {
    setSyncProgress(prev => prev ? {
      ...prev,
      step2_verifyAccount: "in_progress",
      step3_syncMapping: "pending",
      errorMessage: null,
      subaccountsLoadProgress: null,
    } : null);

    const trimmedInput = ibkrAccountId.trim().toLowerCase();

    // Check cache first
    const cached = advisorAccountService.getCachedSubaccounts();
    if (cached) {
      const found = cached.some(sub => sub.accountId.trim().toLowerCase() === trimmedInput);
      if (found) {
        if (!isCurrentSyncRequest(requestId, saveUserId)) return;
        setSyncProgress(prev => prev ? { ...prev, step2_verifyAccount: "success" } : null);
        await runStep3(requestId, saveUserId);
        return;
      }
    }

    // Cache miss or account not in cache -- fetch fresh
    try {
      const result = await advisorAccountService.fetchAllSubaccounts(
        (loaded, total) => {
          if (isCurrentSyncRequest(requestId, saveUserId)) {
            setSyncProgress(prev => prev ? { ...prev, subaccountsLoadProgress: { loaded, total } } : null);
          }
        },
        () => !isCurrentSyncRequest(requestId, saveUserId),
      );

      if (!isCurrentSyncRequest(requestId, saveUserId) || result === null) return;

      const found = result.some(sub => sub.accountId.trim().toLowerCase() === trimmedInput);
      if (!found) {
        setSyncProgress(prev => prev ? {
          ...prev,
          step2_verifyAccount: "error",
          errorMessage: `Account ${ibkrAccountId.trim()} not found in IBKR subaccounts.`,
          subaccountsLoadProgress: null,
        } : null);
        return;
      }

      setSyncProgress(prev => prev ? { ...prev, step2_verifyAccount: "success", subaccountsLoadProgress: null } : null);
    } catch (err: any) {
      if (!isCurrentSyncRequest(requestId, saveUserId)) return;
      setSyncProgress(prev => prev ? {
        ...prev,
        step2_verifyAccount: "error",
        errorMessage: err.response?.data?.error || err.message || "Failed to verify account in IBKR subaccounts.",
        subaccountsLoadProgress: null,
      } : null);
      return;
    }

    await runStep3(requestId, saveUserId);
  };

  const runStep3 = async (requestId: number, saveUserId: string) => {
    setSyncProgress(prev => prev ? {
      ...prev,
      step3_syncMapping: "in_progress",
      errorMessage: null,
    } : null);

    try {
      await advisorAccountService.syncUserAccount(saveUserId);
      if (!isCurrentSyncRequest(requestId, saveUserId)) return;
      setSyncProgress(prev => prev ? { ...prev, step3_syncMapping: "success" } : null);
      // Account is now synced -- promote into session snapshot so subsequent flag-only saves skip sync
      loadedIbkrAccountIdRef.current = savedPayloadRef.current.ibkrAccountId;
      setLinkedAccountStatus("linked");
    } catch (err: any) {
      if (!isCurrentSyncRequest(requestId, saveUserId)) return;
      console.error("Failed to sync account mapping:", err);
      setSyncProgress(prev => prev ? {
        ...prev,
        step3_syncMapping: "error",
        errorMessage: `Failed to sync account mapping: ${err.response?.data?.error || err.message || "Unknown error"}`,
      } : null);
    }
  };

  const hasPayloadDrifted = (): boolean => {
    const saved = savedPayloadRef.current;
    if (ibkrAccountId.trim() !== saved.ibkrAccountId) return true;
    const currentData = buildDataPayload();
    return JSON.stringify(currentData) !== JSON.stringify(saved.data);
  };

  const computeIsDirty = (): boolean => {
    const isUserNameDirty = canEditUserName && userNameValue !== cleanUserNameRef.current;
    const snap = cleanSnapshotRef.current;
    if (!snap) return isUserNameDirty;
    return (
      isUserNameDirty ||
      ibkrAccountId !== snap.ibkrAccountId ||
      isbeta !== snap.isbeta ||
      iscustomer !== snap.iscustomer ||
      isretirement !== snap.isretirement ||
      tradingenabled !== snap.tradingenabled ||
      userTradingEnabled !== snap.userTradingEnabled ||
      cashBasedDeployment !== snap.cashBasedDeployment ||
      !shallowEqualStringRecord(additionalRootValues, snap.additionalRootValues) ||
      !shallowEqualStringRecord(additionalUserSettingValues, snap.additionalUserSettingValues)
    );
  };

  const handleCloseRequest = () => {
    if (userNameSaving) return;
    if (computeIsDirty()) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  const handleDiscardAndClose = () => {
    setShowDiscardConfirm(false);
    onClose();
  };

  const handleRetryStep = async (step: 1 | 2 | 3) => {
    if (!userId) return;
    const formDrifted = hasPayloadDrifted();
    if (step === 1 || formDrifted) {
      handleSave();
    } else {
      const requestId = syncRequestIdRef.current;
      if (step === 2) {
        advisorAccountService.clearSubaccountsCache();
        await runStep2(requestId, userId);
      } else {
        await runStep3(requestId, userId);
      }
    }
  };

  const handleDelete = async () => {
    if (!userId || !config) {
      console.log("Delete blocked: userId or config missing", { userId, config });
      return;
    }

    const requestId = ++syncRequestIdRef.current;
    const deleteUserId = userId;
    try {
      setLoading(true);
      setError(null);
      setShowDeleteConfirm(false);
      console.log("Calling deleteUserConfiguration for userId:", userId);
      await userConfigurationService.deleteUserConfiguration(deleteUserId);
      if (!isCurrentSyncRequest(requestId, deleteUserId)) return;
      console.log("Delete successful, closing modal");
      onClose();
    } catch (err: any) {
      if (!isCurrentSyncRequest(requestId, deleteUserId)) return;
      console.error("Failed to delete user configuration:", err);
      setError(err.response?.data?.error || err.message || "Failed to delete configuration");
    } finally {
      if (isCurrentSyncRequest(requestId, deleteUserId)) setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isSyncInProgress = syncProgress !== null && (
    syncProgress.step1_saveConfig === "in_progress" ||
    syncProgress.step2_verifyAccount === "in_progress" ||
    syncProgress.step3_syncMapping === "in_progress"
  );
  const additionalRootSettings = getAdditionalRootSettings(configurationSchema);
  const userSettingDescriptors = getUserSettingDescriptors(configurationSchema);
  const firstRootInputError = additionalRootSettings
    .map((setting) => getInputValidationError(setting, additionalRootValues[setting.name]))
    .find((message): message is string => message !== null);
  const isSaveDisabled =
    loading ||
    userNameSaving ||
    retirementConfirm !== null ||
    customerConfirm !== null ||
    customerCheckLoading ||
    isSyncInProgress ||
    Boolean(firstRootInputError);

  const flagButtonClass = (active: boolean, compact = false): string => {
    const sizeClass = compact
      ? "shrink-0 whitespace-nowrap px-2.5 py-1 text-xs"
      : "px-4 py-1.5 text-sm";
    if (appearance === "alpha") return `${sizeClass} rounded-v7-control border font-medium transition-colors ${active ?"border-v7-ink bg-v7-ink text-v7-white" : "border-v7-ink/25 bg-v7-white text-v7-ink-soft hover:bg-v7-paper"}`;
    return active
      ? `${sizeClass} rounded-md font-medium bg-rp-amber text-white`
      : isDarkMode
        ? `${sizeClass} rounded-md font-medium border border-rp-separator text-gray-300 hover:border-gray-400`
        : `${sizeClass} rounded-md font-medium border border-gray-200 text-gray-600 hover:border-gray-300`;
  };

  const getBuiltInUserSettingLabel = (name: string): string => {
    if (name === "user_tradingenabled") return "User Trading Enabled";
    if (name === CASH_BASED_DEPLOYMENT_KEY) return "New Cash Deployment";
    return formatSettingLabel(name);
  };

  const getUserSettingValue = (setting: ConfigurationSettingDescriptor): string | null => {
    if (setting.name === "user_tradingenabled") return userTradingEnabled;
    if (setting.name === CASH_BASED_DEPLOYMENT_KEY) return cashBasedDeployment;
    return additionalUserSettingValues[setting.name] ?? null;
  };

  const readOnlyUserSettingDescriptors = userSettingDescriptors.filter((setting) =>
    !ADMIN_EDITABLE_USER_SETTINGS_KEYS.has(setting.name)
  );
  const cashDeploymentSetting = userSettingDescriptors.find(
    (setting) => setting.name === CASH_BASED_DEPLOYMENT_KEY
  );

  const inlineConfirmClass = appearance === "alpha" ? "mt-3 p-3 rounded-v7-panel border border-v7-bronze/30 bg-v7-paper" : isDarkMode
    ? "mt-3 p-3 rounded-lg border border-yellow-700 bg-yellow-900/20"
    : "mt-3 p-3 rounded-lg border border-yellow-300 bg-yellow-50";

  const inlineConfirmTitleClass = appearance === "alpha"? `text-sm font-medium mb-1 text-v7-bronze-dark` : `text-sm font-medium mb-1 ${isDarkMode ?"text-yellow-300" : "text-yellow-800"}`;
  const inlineConfirmBodyClass = appearance === "alpha"? `text-xs mb-3 text-v7-bronze-dark` : `text-xs mb-3 ${isDarkMode ?"text-yellow-400" : "text-yellow-700"}`;

  return (
    <>
    <AdminModalShell appearance={appearance} isOpen={isOpen} onClose={handleCloseRequest}
      title={`Configuration for ${userName || userId}`} closeLabel="Close configuration"
      closeDisabled={userNameSaving || showDiscardConfirm} maxWidthClassName="max-w-2xl"
      footer={
        <div className={appearance === "alpha"? `flex items-center justify-between flex-wrap gap-3 border-v7-ink/15` : `flex items-center justify-between flex-wrap gap-3 ${isDarkMode ?"border-rp-separator" : "border-gray-200"}`}>
          {showDeleteConfirm ? (
            <>
              <div className={appearance === "alpha"? `flex items-center gap-2 text-v7-bronze-dark` : `flex items-center gap-2 ${isDarkMode ?"text-yellow-400" : "text-yellow-600"}`}>
                <span className="font-medium">Are you sure you want to delete this configuration?</span>
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={loading || userNameSaving || isSyncInProgress}
                  className={appearance === "alpha" ? `px-4 py-2 rounded-v7-panel transition-colors disabled:opacity-50 text-v7-ink-soft hover:text-v7-ink-soft hover:bg-v7-paper` : `px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                    isDarkMode
                      ? "text-gray-300 hover:text-white hover:bg-rp-surface"
                      : "text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={loading || userNameSaving || isSyncInProgress}
                  className={appearance === "alpha" ? "px-4 py-2 bg-v7-rust text-v7-white rounded-v7-panel hover:bg-v7-rust transition-colors disabled:opacity-50" : "px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"}
                >
                  {loading ? "Deleting..." : "Confirm Delete"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div>
                {config && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm(true);
                    }}
                    disabled={loading || userNameSaving || isSyncInProgress}
                    className={appearance === "alpha" ? `px-4 py-2 rounded-v7-panel transition-colors disabled:opacity-50 text-v7-rust hover:text-v7-rust hover:bg-v7-rust/5` : `px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                      isDarkMode
                        ? "text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        : "text-red-600 hover:text-red-700 hover:bg-red-50"
                    }`}
                  >
                    Delete Configuration
                  </button>
                )}
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleCloseRequest}
                  disabled={loading || userNameSaving}
                  className={appearance === "alpha" ? `px-4 py-2 rounded-v7-panel transition-colors disabled:opacity-50 text-v7-ink-soft hover:text-v7-ink-soft hover:bg-v7-paper` : `px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
                    isDarkMode
                      ? "text-gray-300 hover:text-white hover:bg-rp-surface"
                      : "text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                  className={appearance === "alpha" ? "px-4 py-2 bg-v7-ink text-v7-white font-semibold rounded-v7-panel hover:bg-v7-bronze-dark transition-colors disabled:opacity-50 flex items-center" : "px-4 py-2 bg-rp-amber text-white font-semibold rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 flex items-center"}
                >
                  {loading ? (
                    <div className={appearance === "alpha" ? "animate-spin rounded-full h-4 w-4 border-b-2 border-v7-white mr-2" : "animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"}></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </button>
              </div>
            </>
          )}
        </div>

      }>
        {/* Content */}
        <div className="space-y-6">
          {loading && !config && !error ? (
            <div className="flex justify-center py-8">
              <div className={appearance === "alpha" ? "animate-spin rounded-full h-8 w-8 border-b-2 border-v7-bronze/30" : "animate-spin rounded-full h-8 w-8 border-b-2 border-rp-amber"}></div>
            </div>
          ) : (
            <>
              {error && (
                <div className={appearance === "alpha"? `p-4 rounded-v7-panel border bg-v7-rust/5 border-v7-rust/30 text-v7-rust` : `p-4 rounded-lg border ${isDarkMode ?"bg-red-900/20 border-red-800 text-red-400" : "bg-red-50 border-red-200 text-red-700"}`}>
                  {error}
                </div>
              )}

              {canEditUserName && (
                <div className="ph-mask" data-testid="user-name-editor">
                  <label
                    htmlFor={`managed-user-name-${userId}`}
                    className={appearance === "alpha"? `mb-1 block text-sm font-medium text-v7-ink-soft` : `mb-1 block text-sm font-medium ${isDarkMode ?"text-gray-300" : "text-gray-700"}`}
                  >
                    Full Name
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id={`managed-user-name-${userId}`}
                      type="text"
                      value={userNameValue}
                      onChange={(event) => {
                        setUserNameValue(event.target.value);
                        setUserNameError(null);
                        setUserNameSaved(false);
                      }}
                      disabled={userNameSaving || isSyncInProgress}
                      placeholder="Full name"
                      className={appearance === "alpha" ? `min-w-0 flex-1 rounded-v7-panel border px-3 py-2 text-sm transition-all focus:border-v7-bronze/30 focus:outline-none focus:ring-1 focus:ring-v7-bronze-dark disabled:opacity-50 border-v7-ink/15 bg-v7-white text-v7-ink placeholder-v7-ink/45` : `min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber disabled:opacity-50 ${
                        isDarkMode
                          ? "border-rp-separator bg-rp-surface-raised text-white placeholder-gray-500"
                          : "border-gray-200 bg-white text-gray-900 placeholder-gray-400"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => void handleUserNameUpdate()}
                      disabled={
                        userNameSaving ||
                        isSyncInProgress ||
                        userNameValue === cleanUserNameRef.current
                      }
                      className={appearance === "alpha" ? "shrink-0 rounded-v7-panel border border-v7-ink/15 bg-v7-white px-3 py-2 text-sm font-semibold text-v7-ink-soft transition-colors hover:border-v7-bronze/30 hover:text-v7-bronze-dark disabled:cursor-not-allowed disabled:opacity-50" : "shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-rp-amber hover:text-rp-amber disabled:cursor-not-allowed disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-rp-amber dark:hover:text-rp-amber"}
                    >
                      {userNameSaving ? "Updating..." : "Update Name"}
                    </button>
                  </div>
                  {userNameError && (
                    <p role="alert" className={appearance === "alpha" ? "mt-2 text-xs text-v7-rust" : "mt-2 text-xs text-red-600 dark:text-red-500"}>
                      {userNameError}
                    </p>
                  )}
                  {userNameSaved && (
                    <p role="status" className={appearance === "alpha" ? "mt-2 text-xs text-v7-success" : "mt-2 text-xs text-green-600 dark:text-green-500"}>
                      Name updated successfully.
                    </p>
                  )}
                </div>
              )}

              {/* IBKR Account ID */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className={appearance === "alpha"? `block text-sm font-medium text-v7-ink-soft` : `block text-sm font-medium ${isDarkMode ?"text-gray-300" : "text-gray-700"}`}>
                    IBKR Account ID
                  </label>
                  {linkedAccountStatus === "linked" && (
                    <span className={appearance === "alpha" ? "px-2 py-0.5 text-xs font-medium rounded-full bg-v7-success/10 text-v7-success" : "px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"}>
                      Linked
                    </span>
                  )}
                  {linkedAccountStatus === "not_linked" && (
                    <span className={appearance === "alpha" ? "px-2 py-0.5 text-xs font-medium rounded-full bg-v7-paper text-v7-bronze-dark" : "px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"}>
                      Not linked
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  value={ibkrAccountId}
                  onChange={(e) => {
                    setIbkrAccountId(e.target.value);
                    setIsbeta(e.target.value.trim() ? "yes" : "no");
                    // Invalidate any in-flight pre-check and clear stale badge
                    ++linkedAccountCheckIdRef.current;
                    setLinkedAccountStatus("unknown");
                  }}
                  disabled={isSyncInProgress}
                  placeholder="e.g. U1234567"
                  className={appearance === "alpha" ? `w-full px-3 py-2 border rounded-v7-panel text-sm focus:outline-none focus:border-v7-bronze/30 focus:ring-1 focus:ring-v7-bronze-dark transition-all bg-v7-white border-v7-ink/15 text-v7-ink placeholder-v7-ink/45` : `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all ${
                    isDarkMode ? "bg-rp-surface-raised border-rp-separator text-white placeholder-gray-500" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"
                  }`}
                />
              </div>

              {/* Configuration Flags */}
              <div>
                <label className={appearance === "alpha"? `block text-sm font-medium mb-3 text-v7-ink-soft` : `block text-sm font-medium mb-3 ${isDarkMode ?"text-gray-300" : "text-gray-700"}`}>
                  Configuration Flags
                </label>
                <div className="space-y-4">
                  {/* isbeta */}
                  <div className={appearance === "alpha" ? "flex flex-wrap items-center justify-between gap-3" : "flex items-center justify-between"}>
                    <div>
                      <span className={appearance === "alpha"? `text-xs font-mono text-v7-ink/60` : `text-xs font-mono ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>isbeta</span>
                      <span className={appearance === "alpha"? `ml-3 text-sm text-v7-ink-soft` : `ml-3 text-sm ${isDarkMode ?"text-gray-300" : "text-gray-700"}`}>Is Beta User</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setIsbeta("unset")}
                        disabled={isSyncInProgress}
                        className={flagButtonClass(isbeta === "unset")}
                      >
                        Unset
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsbeta("yes")}
                        disabled={isSyncInProgress}
                        className={flagButtonClass(isbeta === "yes")}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsbeta("no")}
                        disabled={isSyncInProgress}
                        className={flagButtonClass(isbeta === "no")}
                      >
                        No
                      </button>
                    </div>
                  </div>

                  {/* iscustomer */}
                  <div>
                    <div className={appearance === "alpha" ? "flex flex-wrap items-center justify-between gap-3" : "flex items-center justify-between"}>
                      <div>
                        <span className={appearance === "alpha"? `text-xs font-mono text-v7-ink/60` : `text-xs font-mono ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>iscustomer</span>
                        <span className={appearance === "alpha"? `ml-3 text-sm text-v7-ink-soft` : `ml-3 text-sm ${isDarkMode ?"text-gray-300" : "text-gray-700"}`}>Is Customer</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleCustomerChange("unset")}
                          disabled={customerConfirm !== null || customerCheckLoading || retirementConfirm !== null || isSyncInProgress}
                          className={flagButtonClass(iscustomer === "unset")}
                        >
                          Unset
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCustomerChange("yes")}
                          disabled={customerConfirm !== null || customerCheckLoading || retirementConfirm !== null || isSyncInProgress}
                          className={flagButtonClass(iscustomer === "yes")}
                        >
                          {customerCheckLoading ? "Checking..." : "Yes"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCustomerChange("no")}
                          disabled={customerConfirm !== null || customerCheckLoading || retirementConfirm !== null || isSyncInProgress}
                          className={flagButtonClass(iscustomer === "no")}
                        >
                          No
                        </button>
                      </div>
                    </div>

                    {customerCheckError && (
                      <div className={appearance === "alpha"? `mt-3 p-3 rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5` : `mt-3 p-3 rounded-lg border ${isDarkMode ?"border-red-700 bg-red-900/20" : "border-red-300 bg-red-50"}`}>
                        <p className={appearance === "alpha"? `text-sm text-v7-rust` : `text-sm ${isDarkMode ?"text-red-300" : "text-red-700"}`}>{customerCheckError}</p>
                      </div>
                    )}

                    {customerConfirm !== null && (
                      <div className={inlineConfirmClass}>
                        <p className={inlineConfirmTitleClass}>
                          {customerConfirm === "to-yes"
                            ? "Confirm customer status?"
                            : customerConfirm === "to-unset"
                              ? "Confirm: Clear customer status?"
                              : "Confirm: Remove customer status?"}
                        </p>
                        <p className={inlineConfirmBodyClass}>
                          {customerConfirm === "to-yes" && customerNetLiq !== null
                            ? `Net liquidation: ${formatCurrencyK(customerNetLiq)}. This grants customer access to the platform.`
                            : customerConfirm === "to-unset"
                              ? "Customer status will be unset for this user."
                              : "Customer access will be removed for this user."}
                        </p>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleCustomerConfirm}
                            className={appearance === "alpha" ? "px-3 py-1.5 text-sm font-medium bg-v7-bronze-dark text-v7-white rounded-v7-control hover:bg-v7-bronze-dark transition-colors" : "px-3 py-1.5 text-sm font-medium bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"}
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={handleCustomerCancel}
                            className={appearance === "alpha"? `px-3 py-1.5 text-sm font-medium rounded-v7-control transition-colors text-v7-ink-soft hover:bg-v7-paper` : `px-3 py-1.5 text-sm font-medium rounded transition-colors ${isDarkMode ?"text-gray-300 hover:bg-rp-surface" : "text-gray-600 hover:bg-gray-100"}`}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* isretirement */}
                  <div>
                    <div className={appearance === "alpha" ? "flex flex-wrap items-center justify-between gap-3" : "flex items-center justify-between"}>
                      <div>
                        <span className={appearance === "alpha"? `text-xs font-mono text-v7-ink/60` : `text-xs font-mono ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>isretirement</span>
                        <span className={appearance === "alpha"? `ml-3 text-sm text-v7-ink-soft` : `ml-3 text-sm ${isDarkMode ?"text-gray-300" : "text-gray-700"}`}>Is Retirement Acct</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleRetirementChange("unset")}
                          disabled={retirementConfirm !== null || customerConfirm !== null || customerCheckLoading || isSyncInProgress}
                          className={flagButtonClass(isretirement === "unset")}
                        >
                          Unset
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRetirementChange("yes")}
                          disabled={retirementConfirm !== null || customerConfirm !== null || customerCheckLoading || isSyncInProgress}
                          className={flagButtonClass(isretirement === "yes")}
                        >
                          Yes
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRetirementChange("no")}
                          disabled={retirementConfirm !== null || customerConfirm !== null || customerCheckLoading || isSyncInProgress}
                          className={flagButtonClass(isretirement === "no")}
                        >
                          No
                        </button>
                      </div>
                    </div>

                    {retirementConfirm !== null && (
                      <div className={inlineConfirmClass}>
                        <p className={inlineConfirmTitleClass}>
                          {retirementConfirm === "to-yes"
                            ? "Confirm: Mark as retirement account?"
                            : retirementConfirm === "to-unset"
                              ? "Confirm: Clear retirement account status?"
                              : "Confirm: Remove retirement account status?"}
                        </p>
                        <p className={inlineConfirmBodyClass}>
                          {retirementConfirm === "to-yes"
                            ? "This will block short positions and show the retirement agreement to this user."
                            : retirementConfirm === "to-unset"
                              ? "Retirement account status will be unset for this user."
                              : "Short positions will no longer be blocked for this user."}
                        </p>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleRetirementConfirm}
                            className={appearance === "alpha" ? "px-3 py-1.5 text-sm font-medium bg-v7-bronze-dark text-v7-white rounded-v7-control hover:bg-v7-bronze-dark transition-colors" : "px-3 py-1.5 text-sm font-medium bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"}
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={handleRetirementCancel}
                            className={appearance === "alpha"? `px-3 py-1.5 text-sm font-medium rounded-v7-control transition-colors text-v7-ink-soft hover:bg-v7-paper` : `px-3 py-1.5 text-sm font-medium rounded transition-colors ${isDarkMode ?"text-gray-300 hover:bg-rp-surface" : "text-gray-600 hover:bg-gray-100"}`}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              </div>

              {/* Trading Kill Switch */}
              <div className={appearance === "alpha"? `p-4 rounded-v7-panel border-2 ${iscustomer !=="yes"
    ? "border-v7-ink/15 bg-v7-paper opacity-50" : tradingenabled === "yes"
    ? "border-v7-success/30 bg-v7-success/10" : tradingenabled === "unset"
    ? "border-v7-ink/15 bg-v7-paper" : "border-v7-rust/30 bg-v7-rust/5"}` : `p-4 rounded-lg border-2 ${
                iscustomer !== "yes"
                  ? isDarkMode ? "border-rp-separator bg-rp-surface/50 opacity-50" : "border-gray-200 bg-gray-50 opacity-50"
                  : tradingenabled === "yes"
                    ? isDarkMode ? "border-green-700 bg-green-900/20" : "border-green-300 bg-green-50"
                    : tradingenabled === "unset"
                      ? isDarkMode ? "border-rp-separator bg-rp-surface/50" : "border-gray-200 bg-gray-50"
                      : isDarkMode ? "border-red-700 bg-red-900/20" : "border-red-300 bg-red-50"
              }`}>
                <div className={appearance === "alpha" ? "flex flex-wrap items-center justify-between gap-3" : "flex items-center justify-between"}>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className={appearance === "alpha" ? `w-2.5 h-2.5 rounded-full ${iscustomer !== "yes"
    ? "bg-v7-ink/30" : tradingenabled === "yes"
    ? "bg-v7-success animate-pulse" : "bg-v7-rust"}` : `w-2.5 h-2.5 rounded-full ${
                        iscustomer !== "yes"
                          ? isDarkMode ? "bg-gray-600" : "bg-gray-400"
                          : tradingenabled === "yes"
                            ? "bg-green-500 animate-pulse"
                            : "bg-red-500"
                      }`} />
                      <span className={appearance === "alpha"? `text-sm font-semibold text-v7-ink` : `text-sm font-semibold ${isDarkMode ?"text-gray-100" : "text-gray-900"}`}>
                        Live Trading
                      </span>
                      <span className={appearance === "alpha"? `text-xs font-mono text-v7-ink/60` : `text-xs font-mono ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
                        tradingenabled
                      </span>
                    </div>
                    <p className={appearance === "alpha"? `mt-1 text-xs text-v7-ink/60` : `mt-1 text-xs ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>
                      {iscustomer !== "yes"
                        ? "Requires customer status to enable"
                        : tradingenabled === "yes"
                          ? "Order execution is active for this account"
                          : tradingenabled === "unset"
                            ? "Order execution setting is unset for this account"
                            : "Order execution is halted for this account"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setTradingenabled(tradingenabled === "yes" ? "no" : "yes")}
                    disabled={iscustomer !== "yes" || isSyncInProgress}
                    className={appearance === "alpha" ? `relative inline-flex h-8 w-16 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 ${tradingenabled === "yes"
    ? "bg-v7-success focus:ring-v7-success" : "bg-v7-rust focus:ring-v7-rust"} focus:ring-offset-v7-white` : `relative inline-flex h-8 w-16 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-40 ${
                      tradingenabled === "yes"
                        ? "bg-green-600 focus:ring-green-500"
                        : isDarkMode
                          ? "bg-red-800 focus:ring-red-500"
                          : "bg-red-600 focus:ring-red-500"
                    } ${isDarkMode ? "focus:ring-offset-rp-surface-raised" : "focus:ring-offset-white"}`}
                    role="switch"
                    aria-checked={tradingenabled === "yes"}
                    aria-label="Toggle live trading"
                  >
                    <span className={appearance === "alpha"? `inline-block h-6 w-6 rounded-full bg-v7-white shadow-md transform transition-transform duration-200 ${tradingenabled ==="yes" ? "translate-x-9" : "translate-x-1"}` : `inline-block h-6 w-6 rounded-full bg-white shadow-md transform transition-transform duration-200 ${
                      tradingenabled === "yes" ? "translate-x-9" : "translate-x-1"
                    }`} />
                  </button>
                </div>
              </div>

              {/* Admin Settings */}
              {additionalRootSettings.length > 0 && (
                <div>
                  <label className={appearance === "alpha"? `block text-sm font-medium mb-3 text-v7-ink-soft` : `block text-sm font-medium mb-3 ${isDarkMode ?"text-gray-300" : "text-gray-700"}`}>
                    Admin Settings
                  </label>
                  <div className="space-y-4">
                    {additionalRootSettings.map((setting) => {
                      const value = additionalRootValues[setting.name] ?? getInitialInputValue(setting, undefined);
                      const validationError = getInputValidationError(setting, value);
                      return (
                        <div key={setting.name} className="space-y-2" role="group" aria-label={formatSettingLabel(setting.name)}>
                          <div className={appearance === "alpha" ? "flex flex-wrap items-center justify-between gap-3" : "flex items-center justify-between gap-4"}>
                            <div>
                              <span className={appearance === "alpha"? `text-xs font-mono text-v7-ink/60` : `text-xs font-mono ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>{setting.name}</span>
                              <span className={appearance === "alpha"? `ml-3 text-sm text-v7-ink-soft` : `ml-3 text-sm ${isDarkMode ?"text-gray-300" : "text-gray-700"}`}>
                                {formatSettingLabel(setting.name)}
                              </span>
                              <span className={appearance === "alpha"? `ml-2 text-[10px] uppercase tracking-wider text-v7-ink/60` : `ml-2 text-[10px] uppercase tracking-wider ${isDarkMode ?"text-gray-600" : "text-gray-400"}`}>
                                {setting.type}
                              </span>
                            </div>
                            {setting.type === "boolean" ? (
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => setAdditionalRootValues((prev) => ({ ...prev, [setting.name]: "unset" }))}
                                  disabled={isSyncInProgress}
                                  className={flagButtonClass(value === "unset")}
                                >
                                  Unset
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setAdditionalRootValues((prev) => ({ ...prev, [setting.name]: "yes" }))}
                                  disabled={isSyncInProgress}
                                  className={flagButtonClass(value === "yes")}
                                >
                                  Yes
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setAdditionalRootValues((prev) => ({ ...prev, [setting.name]: "no" }))}
                                  disabled={isSyncInProgress}
                                  className={flagButtonClass(value === "no")}
                                >
                                  No
                                </button>
                              </div>
                            ) : (
                              <input
                                type={setting.type === "integer" ? "number" : "text"}
                                step={setting.type === "integer" ? 1 : undefined}
                                value={value}
                                onChange={(event) =>
                                  setAdditionalRootValues((prev) => ({ ...prev, [setting.name]: event.target.value }))
                                }
                                onWheel={setting.type === "integer" ? preventScrollChange : undefined}
                                disabled={isSyncInProgress}
                                placeholder={setting.type === "integer" ? "Whole number" : "Text value"}
                                className={appearance === "alpha" ? `w-full sm:w-56 px-3 py-2 border rounded-v7-control text-sm focus:outline-none focus:border-v7-bronze/30 focus:ring-1 focus:ring-v7-bronze-dark transition-all disabled:opacity-50 bg-v7-white border-v7-ink/15 text-v7-ink placeholder-v7-ink/45` : `w-56 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-rp-amber focus:ring-1 focus:ring-rp-amber transition-all disabled:opacity-50 ${
                                  isDarkMode ? "bg-rp-surface-raised border-rp-separator text-white placeholder-gray-500" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"
                                }`}
                              />
                            )}
                          </div>
                          {validationError && (
                            <p className={appearance === "alpha" ? "text-xs text-v7-rust" : "text-xs text-red-600 dark:text-red-500"}>{validationError}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {cashDeploymentSetting && (
                <div>
                  <label className={appearance === "alpha"? `block text-sm font-medium mb-3 text-v7-ink-soft` : `block text-sm font-medium mb-3 ${isDarkMode ?"text-gray-300" : "text-gray-700"}`}>
                    User Settings
                  </label>
                  <div className="space-y-4">
                    <div className={appearance === "alpha" ? "flex flex-wrap items-center justify-between gap-3" : "flex items-center justify-between gap-3"}>
                      <div className="min-w-0 flex-1">
                        <span className={appearance === "alpha"? `text-xs font-mono text-v7-ink/60` : `text-xs font-mono ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
                          {cashDeploymentSetting.name}
                        </span>
                        <span className={appearance === "alpha"? `ml-3 text-sm text-v7-ink-soft` : `ml-3 text-sm ${isDarkMode ?"text-gray-300" : "text-gray-700"}`}>
                          {getBuiltInUserSettingLabel(cashDeploymentSetting.name)}
                        </span>
                        <span className={appearance === "alpha"? `ml-2 text-[10px] uppercase tracking-wider text-v7-ink/60` : `ml-2 text-[10px] uppercase tracking-wider ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
                          {cashDeploymentSetting.type}
                        </span>
                      </div>
                      <div className="flex shrink-0 flex-nowrap items-center justify-end gap-1.5">
                        {([
                          ["unset", "Default"],
                          ["auto-deploy", "Auto-deploy"],
                          ["reserve", "Reserve"],
                        ] as const).map(([option, label]) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setCashBasedDeployment(option)}
                            disabled={isSyncInProgress}
                            className={flagButtonClass(cashBasedDeployment === option, true)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {readOnlyUserSettingDescriptors.length > 0 && (
                <div className="opacity-50">
                  <div className="flex items-center gap-2 mb-3">
                    <label className={appearance === "alpha"? `block text-sm font-medium text-v7-ink/60` : `block text-sm font-medium ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
                      User Settings
                    </label>
                    <span className={appearance === "alpha"? `text-xs px-1.5 py-0.5 rounded-v7-control bg-v7-paper text-v7-ink/60` : `text-xs px-1.5 py-0.5 rounded ${isDarkMode ?"bg-rp-surface text-gray-600" : "bg-gray-100 text-gray-400"}`}>
                      read-only
                    </span>
                  </div>
                  <div className="space-y-4">
                    {readOnlyUserSettingDescriptors.map((setting) => {
                      const value = getUserSettingValue(setting);
                      const isUnset = value === null || value === "" || value === "unset";
                      return (
                        <div key={setting.name} className={appearance === "alpha" ? "flex flex-wrap items-center justify-between gap-3" : "flex items-center justify-between"}>
                          <div>
                            <span className={appearance === "alpha"? `text-xs font-mono text-v7-ink/60` : `text-xs font-mono ${isDarkMode ?"text-gray-600" : "text-gray-400"}`}>{setting.name}</span>
                            <span className={appearance === "alpha"? `ml-3 text-sm text-v7-ink/60` : `ml-3 text-sm ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
                              {getBuiltInUserSettingLabel(setting.name)}
                            </span>
                            <span className={appearance === "alpha"? `ml-2 text-[10px] uppercase tracking-wider text-v7-ink/60` : `ml-2 text-[10px] uppercase tracking-wider ${isDarkMode ?"text-gray-600" : "text-gray-400"}`}>
                              {setting.type}
                            </span>
                          </div>
                          {setting.type === "boolean" && !isUnset ? (
                            <div className="flex gap-2">
                              <button
                                type="button"
                                disabled
                                className={flagButtonClass(value === "yes")}
                              >
                                True
                              </button>
                              <button
                                type="button"
                                disabled
                                className={flagButtonClass(value === "no")}
                              >
                                False
                              </button>
                            </div>
                          ) : (
                            <span className={appearance === "alpha" ? `px-3 py-1.5 rounded-v7-control text-sm font-medium border border-v7-ink/15 text-v7-ink-soft bg-v7-paper` : `px-3 py-1.5 rounded-md text-sm font-medium border ${
                              isDarkMode
                                ? "border-rp-separator text-gray-300 bg-rp-surface"
                                : "border-gray-200 text-gray-600 bg-gray-50"
                            }`}>
                              {isUnset ? "Unset" : value}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Sync Progress UI */}
              {syncProgress && (
                <div className={appearance === "alpha"? `p-4 rounded-v7-panel border border-v7-ink/15 bg-v7-paper` : `p-4 rounded-lg border ${isDarkMode ?"border-rp-separator bg-rp-surface" : "border-gray-200 bg-gray-50"}`}>
                  <h4 className={appearance === "alpha"? `text-sm font-medium mb-3 text-v7-ink-soft` : `text-sm font-medium mb-3 ${isDarkMode ?"text-white" : "text-gray-700"}`}>
                    Save Progress
                  </h4>
                  <div className="space-y-3">
                    <SyncStepRow appearance={appearance}
                      status={syncProgress.step1_saveConfig}
                      label="Saving configuration..."
                      isDarkMode={isDarkMode}
                    />
                    {syncProgress.ranSyncSteps && (
                      <>
                        <SyncStepRow appearance={appearance}
                          status={syncProgress.step2_verifyAccount}
                          label="Verifying account in IBKR subaccounts..."
                          isDarkMode={isDarkMode}
                          subProgress={syncProgress.subaccountsLoadProgress}
                        />
                        <SyncStepRow appearance={appearance}
                          status={syncProgress.step3_syncMapping}
                          label="Syncing account to database..."
                          isDarkMode={isDarkMode}
                        />
                      </>
                    )}
                  </div>
                  {syncProgress.errorMessage && (
                    <div className={appearance === "alpha"? `mt-3 p-3 rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5` : `mt-3 p-3 rounded-lg border ${isDarkMode ?"border-red-800 bg-red-900/20" : "border-red-200 bg-red-50"}`}>
                      <p className={appearance === "alpha"? `text-sm text-v7-rust` : `text-sm ${isDarkMode ?"text-red-400" : "text-red-700"}`}>{syncProgress.errorMessage}</p>
                      <button
                        type="button"
                        onClick={() => {
                          if (syncProgress.step1_saveConfig === "error") handleRetryStep(1);
                          else if (syncProgress.step2_verifyAccount === "error") handleRetryStep(2);
                          else if (syncProgress.step3_syncMapping === "error") handleRetryStep(3);
                        }}
                        className={appearance === "alpha" ? "mt-2 px-3 py-1.5 text-sm font-medium bg-v7-ink text-v7-white rounded-v7-panel hover:bg-v7-bronze-dark transition-colors" : "mt-2 px-3 py-1.5 text-sm font-medium bg-rp-amber text-white rounded-lg hover:bg-yellow-600 transition-colors"}
                      >
                        Retry
                      </button>
                    </div>
                  )}
                  {syncProgress.step1_saveConfig === "success" && !syncProgress.ranSyncSteps && (
                    <p className={appearance === "alpha"? `mt-3 text-sm text-v7-success` : `mt-3 text-sm ${isDarkMode ?"text-green-400" : "text-green-600"}`}>
                      Configuration saved successfully.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

    </AdminModalShell>
    <ConfirmationModal appearance={appearance}
      isOpen={showDiscardConfirm}
      onClose={() => setShowDiscardConfirm(false)}
      onConfirm={handleDiscardAndClose}
      title="Discard unsaved changes?"
      message="You have unsaved changes to this configuration. Closing now will discard them."
      confirmText="Discard and close"
      cancelText="Keep editing"
      isDestructive
    />
    </>
  );
};

const SyncStepRow: React.FC<{
  appearance?: "default" | "alpha";
  status: SyncStepStatus;
  label: string;
  isDarkMode: boolean;
  subProgress?: { loaded: number; total: number } | null;
}> = ({ appearance = "default", status, label, isDarkMode, subProgress }) => {
  return (
    <div className="flex items-start gap-2">
      <div className="flex-shrink-0 mt-0.5">
        {status === "pending" && (
          <div className={appearance === "alpha"? `w-4 h-4 rounded-full border-2 border-v7-ink/25` : `w-4 h-4 rounded-full border-2 ${isDarkMode ?"border-gray-500" : "border-gray-300"}`} />
        )}
        {status === "in_progress" && (
          <div className={appearance === "alpha" ? "animate-spin rounded-full h-4 w-4 border-b-2 border-v7-bronze/30" : "animate-spin rounded-full h-4 w-4 border-b-2 border-rp-amber"} />
        )}
        {status === "success" && (
          <Check className={appearance === "alpha" ? "w-4 h-4 text-v7-success" : "w-4 h-4 text-green-500"} />
        )}
        {status === "error" && (
          <AlertCircle className={appearance === "alpha" ? "w-4 h-4 text-v7-rust" : "w-4 h-4 text-red-500"} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <span className={appearance === "alpha" ? `text-sm ${status === "pending" ? ("text-v7-ink/60")
    : status === "error" ? ("text-v7-rust")
        : status === "success" ? ("text-v7-success")
            : ("text-v7-ink-soft")}` : `text-sm ${
          status === "pending" ? (isDarkMode ? "text-gray-500" : "text-gray-400")
          : status === "error" ? (isDarkMode ? "text-red-300" : "text-red-700")
          : status === "success" ? (isDarkMode ? "text-green-400" : "text-green-600")
          : (isDarkMode ? "text-gray-200" : "text-gray-700")
        }`}>
          {label}
        </span>
        {status === "in_progress" && subProgress && (
          <span className={appearance === "alpha"? `ml-2 text-xs text-v7-ink/60` : `ml-2 text-xs ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>
            ({subProgress.loaded}/{subProgress.total})
          </span>
        )}
      </div>
    </div>
  );
};

export default UserConfigurationModal;
